/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/scheduleModern/useScheduleModern.ts
 * PURPOSE: Schedule (Lịch cá nhân) data/state hook for ScheduleModern.tsx —
 *          READ-ONLY employee calendar (own shifts only), mirroring mobile's
 *          `mobile/src/hooks/useSchedule.ts`.
 *          2026-09-04: mobile-source-of-truth audit (SC-021-EMP-WEB-SCHEDULE)
 *          found this hook had drifted into a MANAGER-shaped shift-ASSIGNMENT
 *          tool (createShift/assignStaff/handleDrop/compliance-override/
 *          escalation, BFlow mvv_code+bflow_sync_status sync-state polling)
 *          bolted onto what `App.tsx` guards as a plain employee self-service
 *          route (`RequireRole allowedRoles={ALL_TENANT_ROLES}`, same tier as
 *          /attendance + /profile — NOT the separate /manager/schedule route,
 *          which already has its own `ManagerSchedulePage.tsx` +
 *          `useManagerSchedule.ts`). Mobile's employee `ScheduleScreen.tsx`
 *          has ZERO assignment/drag/compliance-override capability — it is
 *          read-only (`useWeekSchedule`/`useMonthSchedule` →
 *          `scheduleApi.getMySchedule()` → `GET /schedules/my-schedule`).
 *          This hook was ALSO calling the wrong, manager-oriented list
 *          endpoint (`GET /schedules/`) and rendering CLIENT-HARDCODED
 *          `required_staff_count: 1` / `assigned_staff_ids: []` on every
 *          card — those two fields were never real backend data (Zero Mock
 *          Tolerance violation). Fix: (1) switch to `GET /schedules/my-schedule`
 *          — same endpoint + same DRAFT/un-approved-EMPLOYEE-proposal
 *          filtering mobile relies on; (2) drop the fabricated staffing
 *          fields, surface the real `branch_name`/`position`/`shift_code`
 *          fields `ScheduleResponse` actually returns; (3) remove the
 *          manager-only assignment/drag/compliance/escalation/BFlow-sync
 *          surface area entirely — it has zero reachable UI on this route
 *          (registry SC-021 gaps G4/G5 asked "reconnect or remove"; the
 *          mobile read-only baseline answers "remove").
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN §Route 3
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Feature Modules, Vietnamese F&B Business Features
 * - Impact: Feature Delivery, Code Organization, Modularity
 * - Stakeholders: Product Team, Development Team, End Users
 *
 * @technical-architecture
 * - Layer: Feature Layer, Business Modules
 * - Pattern: Feature-Based Architecture, Module Encapsulation
 * - Dependencies: React 18, TypeScript, Feature-Specific Libraries
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/
 * - Business spec: docs/01-planning/02-uat-len-lich-cham-cong/BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN.md §Route 3
 * - Mobile parity source: mobile/src/hooks/useSchedule.ts + mobile/src/screens/schedule/ScheduleScreen.tsx
 * - Registry: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md `SC-021-EMP-WEB-SCHEDULE`
 *  */

import { useState, useCallback, useEffect } from 'react';
import { apiRequest } from '@/services/api/config';
import { useAuthStore } from '@/store/auth';
import { toLocalISODate } from '@/utils/date';

// ─────────────────────────────────────────────────────────────────────
// 🔄 ISS-EMP-3 fix (2026-08-16) — raw shift_type enum leaking into the UI
// WHAT: `Schedule.shift_type` (backend/app/models/schedule.py:36-44, `ShiftType`)
//   is a NATIVE Postgres enum column restricted to exactly 7 legacy English
//   values (morning/afternoon/evening/night/full_day/flexible/split) — this is
//   a DIFFERENT, closed enum from the tenant `shift_templates` master-data
//   codes (BFlow-style M1-M12/A1-A11/N1-N10). Because the column is a native
//   PG enum, `GET /schedules/my-schedule` can only ever return one of these 7
//   strings (or null) for shift_type — the set below is therefore COMPLETE.
// WHERE: loadShifts() below — `title` falls back to raw `shift_type` when
//   `notes` is empty.
// WHY: When a schedule has no `notes`, the raw enum code was rendered
//   directly as the shift card title ("morning", "split"...) instead of a VI
//   label — violates the "mã enum không được làm nhãn hiển thị" rule
//   (EMPLOYEE-CODER-NOTES.md §5).
// FIX: Translate through the canonical VI label table registered in
//   docs/02-design/04-ui-ux-design/UI-LABELS-VI-ENUM.md §9c "Hệ legacy EN"
//   (values copied verbatim from that SSOT). Codes outside this 7-value set
//   pass through unchanged.
// DESIGN BASELINE: docs/02-design/04-ui-ux-design/UI-LABELS-VI-ENUM.md §9c.
// ─────────────────────────────────────────────────────────────────────
const SHIFT_TYPE_LABELS_LEGACY: Record<string, string> = {
  morning: 'Ca sáng',
  afternoon: 'Ca chiều',
  evening: 'Ca tối',
  night: 'Ca đêm',
  full_day: 'Cả ngày',
  flexible: 'Linh hoạt',
  split: 'Ca gãy',
};

const translateShiftTypeLabel = (code: string): string =>
  SHIFT_TYPE_LABELS_LEGACY[code] || code;

export type ViewMode = 'week' | 'month' | 'day';
export type ShiftStatus = 'draft' | 'published' | 'in_progress' | 'completed';

export interface Shift {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  start_time: string; // HH:MM
  end_time: string; // HH:MM
  status: ShiftStatus;
  shift_type: string; // Master data code (CA_SANG etc.) or legacy English (morning etc.)
  // Real fields from ScheduleResponse (backend/app/schemas/schedule.py) — NOT
  // client-fabricated, unlike the `required_staff_count`/`assigned_staff_ids`
  // this file used to hardcode (see file header 2026-09-04 note above).
  shift_code?: string;   // Short code (M5/A2/N7...) from ShiftTemplate.code
  branch_name?: string;  // Organization.name for Schedule.organization_id — VĐ1
                          // cross-branch dispatch means this can differ from
                          // the employee's home branch; MUST show real value.
  position?: string;
}

export interface ScheduleState {
  viewMode: ViewMode;
  selectedDate: Date;
  shifts: Shift[];
  loading: boolean;
  error: string | null;
}

export const useScheduleModern = () => {
  const { accessToken } = useAuthStore();
  const [state, setState] = useState<ScheduleState>({
    viewMode: 'month', // G5: BR-SCHD-001 — Monthly calendar as default
    selectedDate: new Date(),
    shifts: [],
    loading: false,
    error: null,
  });

  // BS-301 Route 3 §1 (2026-08-17): distinguish the two empty-state Gherkin
  // scenarios — "nhân viên mới chưa từng có lịch" (FRESH) vs "tháng đang xem
  // chưa xếp lịch" (EMPTY, employee has shifts in OTHER months). `null` = not
  // yet determined (checked once on mount, independent of viewMode/selectedDate
  // so switching months doesn't reissue this wide-range probe).
  const [hasAnyShiftEver, setHasAnyShiftEver] = useState<boolean | null>(null);

  const checkHasAnyShiftEver = useCallback(async () => {
    try {
      const start = new Date();
      start.setFullYear(start.getFullYear() - 2);
      const end = new Date();
      end.setFullYear(end.getFullYear() + 1);
      const params = new URLSearchParams({ start_date: toLocalISODate(start), end_date: toLocalISODate(end), limit: '1' });
      const response = await apiRequest<{ schedules: unknown[]; total?: number }>(
        `/schedules/?${params}`,
        { token: accessToken || undefined }
      );
      setHasAnyShiftEver((response.schedules?.length ?? 0) > 0 || (response.total ?? 0) > 0);
    } catch {
      // Unknown (fetch failed) — leave null; the page falls back to the
      // "EMPTY this period" copy rather than incorrectly claiming FRESH.
      setHasAnyShiftEver(null);
    }
  }, [accessToken]);

  // Load shifts for current view — employee's OWN schedule only.
  const loadShifts = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const startDate = new Date(state.selectedDate);
      const endDate = new Date(state.selectedDate);

      if (state.viewMode === 'week') {
        startDate.setDate(startDate.getDate() - startDate.getDay() + 1);
        endDate.setDate(startDate.getDate() + 6);
      } else if (state.viewMode === 'month') {
        startDate.setDate(1);
        endDate.setMonth(endDate.getMonth() + 1, 0);
      }

      const params = new URLSearchParams({
        start_date: toLocalISODate(startDate),
        end_date: toLocalISODate(endDate),
      });
      // 2026-09-04 fix (see file header): was `GET /schedules/` — the generic
      // manager-oriented list endpoint (`list_schedules`, backing
      // `POST /schedules/`-style shift-ASSIGNMENT tooling). Employee's own
      // calendar must use `GET /schedules/my-schedule` — the SAME endpoint
      // mobile's `scheduleApi.getMySchedule()` calls — which correctly
      // excludes DRAFT (not-yet-published) schedules and un-approved
      // EMPLOYEE-proposed schedules (backend/app/api/v1/schedule.py
      // `get_my_schedule`).
      const response = await apiRequest<Array<{
        id: string;
        notes?: string;
        shift_type?: string;
        shift_code?: string;
        branch_name?: string;
        position?: string;
        date: string;
        start_time: string;
        end_time: string;
        status?: string;
      }>>(
        `/schedules/my-schedule?${params}`,
        { token: accessToken || undefined }
      );

      const mappedShifts: Shift[] = (response || []).map(s => ({
        id: s.id,
        // ISS-EMP-3 fix (see SHIFT_TYPE_LABELS_LEGACY above): translate the
        // raw shift_type enum before it becomes the display title.
        title: s.notes || (s.shift_type ? translateShiftTypeLabel(s.shift_type) : '') || 'Ca làm việc',
        date: s.date,
        start_time: s.start_time.length > 5 ? s.start_time.slice(0, 5) : s.start_time,
        end_time: s.end_time.length > 5 ? s.end_time.slice(0, 5) : s.end_time,
        status: (s.status || 'published') as ShiftStatus,
        shift_type: s.shift_type || '',
        shift_code: s.shift_code,
        branch_name: s.branch_name,
        position: s.position,
      }));
      setState(prev => ({ ...prev, shifts: mappedShifts, loading: false }));
    } catch {
      setState(prev => ({
        ...prev,
        error: 'Không thể tải dữ liệu lịch làm việc',
        loading: false,
      }));
    }
  }, [state.selectedDate, state.viewMode, accessToken]);

  // View mode controls
  const setViewMode = useCallback((mode: ViewMode) => {
    setState(prev => ({ ...prev, viewMode: mode }));
  }, []);

  const setSelectedDate = useCallback((date: Date) => {
    setState(prev => ({ ...prev, selectedDate: date }));
  }, []);

  // Load shifts when view changes
  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  // BS-301 Route 3 §1 (2026-08-17): FRESH-vs-EMPTY probe, once on mount.
  useEffect(() => {
    checkHasAnyShiftEver();
  }, [checkHasAnyShiftEver]);

  return {
    ...state,
    hasAnyShiftEver,
    setViewMode,
    setSelectedDate,
    loadShifts,
  };
};
